// BillingSuccess Page
// Handles successful Stripe payment redirects

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheckCircle, FiLoader } from 'react-icons/fi';
import { Button } from '../../components/ui/Button/Button';
import { Card } from '../../components/ui/Card/Card';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useBilling } from '../../hooks/useBilling';
import { useToast } from '../../hooks/useToast';
import { verifyCheckoutSession } from '../../api/billing';
import styles from './BillingSuccess.module.css';

import { logger } from '../../utils/logger';

export function BillingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { currentOrganization } = useOrganization();
  const { billing, refetch: refetchBilling } = useBilling(currentOrganization?.id || '');
  const toast = useToast();
  const [verifying, setVerifying] = useState(true);
  const [hasVerified, setHasVerified] = useState(false);
  const [verificationResult, setVerificationResult] = useState<any>(null);

  useEffect(() => {
    // Only verify once
    if (hasVerified) return;

    // Wait for sessionId to be available
    if (!sessionId) {
      logger.log('⚠️ No session_id in URL');
      setVerifying(false);
      setHasVerified(true);
      return;
    }

    let isMounted = true;

    const verifyUpgrade = async () => {
      try {
        logger.log('🔍 Calling backend to verify session:', sessionId);
        logger.log('📞 API call: GET /auth/billing/success?session_id=' + sessionId);

        // Call backend to verify session and process upgrade
        // The backend will verify with Stripe and update the plan
        const result = await verifyCheckoutSession(sessionId);
        logger.log('✅ Backend verification response:', result);

        // Store verification result to display plan name
        if (isMounted) {
          setVerificationResult(result);
        }

        // Wait 2 seconds for backend to fully process
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Refetch billing to get updated plan
        if (isMounted && currentOrganization?.id) {
          logger.log('🔄 Refetching billing info...');
          await refetchBilling();
          logger.log('✅ Billing info refreshed');
          logger.log('📊 Plan from verification:', result.plan_name);
          logger.log('📊 Plan from billing API:', billing?.plan.name);
        }

        if (isMounted) {
          setVerifying(false);
          setHasVerified(true);
          toast.success('Payment Successful!', `Upgraded to ${result.plan_name || 'new plan'}`);
        }

      } catch (error) {
        logger.error('❌ Error verifying upgrade:', error);

        if (isMounted) {
          setVerifying(false);
          setHasVerified(true);
          toast.warning('Payment Received', 'Your upgrade is being processed. Please refresh in a moment.');
        }
      }
    };

    verifyUpgrade();

    // Cleanup
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, hasVerified]);

  return (
    <div className={styles.container}>
      <Card className={styles.successCard}>
        <div className={styles.content}>
          {verifying ? (
            <>
              <div className={styles.iconWrapper}>
                <FiLoader className={styles.spinner} />
              </div>
              <h1 className={styles.title}>Verifying Payment...</h1>
              <p className={styles.message}>
                Please wait while we confirm your upgrade.
              </p>
            </>
          ) : (
            <>
              <div className={styles.iconWrapper}>
                <FiCheckCircle className={styles.successIcon} />
              </div>
              <h1 className={styles.title}>Payment Successful!</h1>
              <p className={styles.message}>
                Your plan has been upgraded successfully.
              </p>

              {/* Show plan from verification response first, then from billing */}
              {(verificationResult?.plan_name || billing?.plan.name) && (
                <div className={styles.planInfo}>
                  <p className={styles.planLabel}>New Plan:</p>
                  <p className={styles.planName}>
                    {verificationResult?.plan_name || billing?.plan.name}
                  </p>
                  {verificationResult?.upgrade_processed && (
                    <p className={styles.upgradeStatus}>✅ Upgrade processed successfully</p>
                  )}
                </div>
              )}

              {sessionId && (
                <p className={styles.sessionId}>
                  Session ID: <code>{sessionId}</code>
                </p>
              )}

              <div className={styles.actions}>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate('/admin/billing')}
                >
                  Go to Billing
                </Button>
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

